<template>
	<div class="progress-dial-v2">
		<!-- Top section: ring + text -->
		<div class="pdv2-top">
			<!-- Circular progress ring -->
			<div class="pdv2-ring-wrapper" @click="animate">
				<svg class="pdv2-svg" viewBox="0 0 70 70">
					<defs>
						<linearGradient :id="gradientId + '-1'" gradientUnits="userSpaceOnUse"
							:x1="arcStart.x" :y1="arcStart.y" :x2="arcMid.x" :y2="arcMid.y">
							<stop offset="0%" stop-color="#FCE5D4" />
							<stop offset="100%" stop-color="#E1C6C8" />
						</linearGradient>
						<linearGradient :id="gradientId + '-2'" gradientUnits="userSpaceOnUse"
							:x1="arcMid.x" :y1="arcMid.y" :x2="arcEnd.x" :y2="arcEnd.y">
							<stop offset="0%" stop-color="#E1C6C8" />
							<stop offset="100%" :stop-color="endColour" />
						</linearGradient>
					</defs>
					<!-- Background track -->
					<circle cx="35" cy="35" r="33" fill="none" stroke="#F2F2F2" stroke-width="3.8" />
					<!-- First half of progress -->
					<circle v-if="currentProgress > 0.005"
						cx="35" cy="35" r="33" fill="none"
						:stroke="'url(#' + gradientId + '-1)'"
						stroke-width="3.8"
						stroke-linecap="round"
						:stroke-dasharray="halfArc + ' ' + circumference"
						:stroke-dashoffset="0"
					/>
					<!-- Second half of progress -->
					<circle v-if="currentProgress > 0.005"
						cx="35" cy="35" r="33" fill="none"
						:stroke="'url(#' + gradientId + '-2)'"
						stroke-width="3.8"
						stroke-linecap="round"
						:stroke-dasharray="halfArc + ' ' + circumference"
						:stroke-dashoffset="'-' + halfArc"
					/>
				</svg>
				<div class="pdv2-ring-inner">
					<img v-if="image" :src="image" class="pdv2-icon" />
				</div>
			</div>

			<!-- Text info -->
			<div class="pdv2-info">
				<span class="pdv2-title">{{ title }}</span>
				<div class="pdv2-date-row">
					<span class="pdv2-date">{{ formattedDate }}</span>
					<span class="pdv2-days">in {{ daysRemaining }} days</span>
				</div>
			</div>
		</div>

		<!-- Info message bar -->
		<div v-if="infoMessage" class="pdv2-message">
			<img :src="require('@/assets/info-16px.svg')" class="pdv2-message-icon" />
			<span class="pdv2-message-text" v-html="infoMessage"></span>
		</div>
	</div>
</template>

<script>
const R = 33;
const CIRCUMFERENCE = 2 * Math.PI * R;

export default {
	name: 'ProgressDialV2',
	props: {
		image: { type: String, default: '' },
		fromDate: { type: String, required: true },
		toDate: { type: String, required: true },
		title: { type: String, default: 'Renewal' },
		infoMessage: { type: String, default: '' },
		animationDuration: { type: Number, default: 1500 }
	},
	data() {
		return {
			currentProgress: 0,
			circumference: CIRCUMFERENCE,
			gradientId: 'pdv2-' + Math.random().toString(36).substring(2, 11)
		};
	},
	computed: {
		total() {
			const from = new Date(this.fromDate);
			const to = new Date(this.toDate);
			return Math.round((to - from) / (1000 * 60 * 60 * 24));
		},
		daysRemaining() {
			const today = new Date();
			today.setHours(0, 0, 0, 0);
			const to = new Date(this.toDate);
			return Math.max(0, Math.round((to - today) / (1000 * 60 * 60 * 24)));
		},
		targetProgress() {
			if (this.total <= 0) return 0;
			return Math.max(0, Math.min(1, (this.total - this.daysRemaining) / this.total));
		},
		formattedDate() {
			const date = new Date(this.toDate);
			return date.toLocaleDateString('en-GB', { day: 'numeric', month: 'long', year: 'numeric' });
		},
		halfArc() {
			return (this.currentProgress * CIRCUMFERENCE) / 2;
		},
		arcStart() {
			return { x: 35 + R, y: 35 };
		},
		arcMid() {
			const angle = this.currentProgress * Math.PI;
			return {
				x: 35 + R * Math.cos(angle),
				y: 35 + R * Math.sin(angle)
			};
		},
		endColour() {
			return this.daysRemaining <= 30 ? '#9EA2D0' : '#E1C6C8';
		},
		arcEnd() {
			const angle = this.currentProgress * 2 * Math.PI;
			return {
				x: 35 + R * Math.cos(angle),
				y: 35 + R * Math.sin(angle)
			};
		}
	},
	mounted() {
		this.animate();
	},
	methods: {
		animate() {
			this.currentProgress = 0;
			const startTime = performance.now();
			const target = this.targetProgress;
			const duration = this.animationDuration;

			const step = (currentTime) => {
				const elapsed = currentTime - startTime;
				const t = Math.min(elapsed / duration, 1);
				const eased = 1 - Math.pow(1 - t, 3);

				this.currentProgress = target * eased;

				if (t < 1) {
					requestAnimationFrame(step);
				}
			};

			requestAnimationFrame(step);
		}
	},
	watch: {
		fromDate() { this.animate(); },
		toDate() { this.animate(); }
	}
};
</script>

<style scoped>
.progress-dial-v2 {
	display: flex;
	flex-direction: column;
	gap: 16px;
	padding: 16px;
	background: rgba(255, 255, 255, 0.9);
	border-radius: 8px;
}

.pdv2-top {
	display: flex;
	align-items: center;
	gap: 16px;
}

.pdv2-ring-wrapper {
	flex-shrink: 0;
	position: relative;
	width: 70px;
	height: 70px;
}

.pdv2-svg {
	width: 100%;
	height: 100%;
	transform: rotate(-90deg);
}

.pdv2-ring-inner {
	position: absolute;
	top: 50%;
	left: 50%;
	transform: translate(-50%, -50%);
	width: 57px;
	height: 57px;
	border-radius: 50%;
	background: #C6CAE5;
	display: flex;
	align-items: center;
	justify-content: center;
}

.pdv2-icon {
	width: 38px;
	height: 33px;
	object-fit: contain;
}

.pdv2-info {
	display: flex;
	flex-direction: column;
	gap: 4px;
	flex: 1;
}

.pdv2-title {
	font-size: 13px;
	color: var(--brand-purple);
}

.pdv2-date-row {
	display: flex;
	justify-content: space-between;
	gap: 8px;
}

.pdv2-date {
	font-size: 13px;
	color: var(--brand-purple);
}

.pdv2-days {
	font-size: 13px;
	color: var(--brand-purple);
}

.pdv2-message {
	display: flex;
	align-items: center;
	gap: 8px;
	padding: 8px 12px;
	background: rgba(198, 202, 229, 0.5);
	border-radius: 4px;
}

.pdv2-message-icon {
	width: 16px;
	height: 16px;
	flex-shrink: 0;
}

.pdv2-message-text {
	font-size: 11px;
	color: var(--brand-purple);
}
</style>
